import { useEffect } from 'react';

interface SchemaOrgData {
  '@context': string;
  '@type': string;
  [key: string]: any;
}

export const useSchemaOrg = (schemaData: SchemaOrgData | SchemaOrgData[]) => {
  useEffect(() => {
    const scriptId = 'schema-org-jsonld';
    let script = document.getElementById(scriptId) as HTMLScriptElement;
    
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    
    script.textContent = JSON.stringify(schemaData);
    
    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [schemaData]);
};

export const getLocalBusinessSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'The Pilot Copy',
    description: 'Professional copywriting services for businesses in Chicago and West Michigan. We help restaurants, contractors, and service providers transform their marketing with authentic, high-converting copy.',
    url: siteUrl,
    logo: `${siteUrl}/vite.svg`,
    image: `${siteUrl}/vite.svg`,
    telephone: '+1-XXX-XXX-XXXX',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '680 North Lake Shore Drive, Suite 110 - 1702',
      addressLocality: 'Chicago',
      addressRegion: 'IL',
      postalCode: '60611',
      addressCountry: 'US'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 41.8781,
      longitude: -87.6298
    },
    areaServed: [
      {
        '@type': 'City',
        name: 'Chicago',
        '@id': 'https://en.wikipedia.org/wiki/Chicago'
      },
      {
        '@type': 'State',
        name: 'Illinois',
        '@id': 'https://en.wikipedia.org/wiki/Illinois'
      },
      {
        '@type': 'State',
        name: 'Michigan',
        '@id': 'https://en.wikipedia.org/wiki/Michigan'
      }
    ],
    priceRange: '$$',
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '17:00'
      }
    ],
    serviceType: 'Copywriting Services',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Copywriting Services',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Website Copywriting',
            description: 'Professional website copy that converts visitors into customers'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Sales Page Copy',
            description: 'High-converting sales pages that drive revenue'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Marketing Content',
            description: 'Authentic marketing content that builds trust and authority'
          }
        }
      ]
    },
    founder: {
      '@type': 'Person',
      name: 'Sebastian',
      jobTitle: 'Professional Copywriter'
    },
    sameAs: []
  };
};

export const getWebPageSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Chicago Copywriting Services West Michigan | The Pilot Copy',
    description: 'Transform your business with authentic, high-converting copywriting services for Chicago and West Michigan businesses.',
    url: siteUrl,
    inLanguage: 'en-US',
    isPartOf: {
      '@type': 'WebSite',
      name: 'The Pilot Copy',
      url: siteUrl
    },
    about: {
      '@type': 'Thing',
      name: 'Copywriting Services',
      description: 'Professional copywriting for businesses'
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: siteUrl
        }
      ]
    }
  };
};

export const getFAQSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How long does a typical project take?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Most projects are completed within 2-3 weeks from kickoff. Rush options are available for an additional fee.'
        }
      },
      {
        '@type': 'Question',
        name: 'Do you offer revisions?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. All packages include two rounds of revisions to ensure you\'re completely satisfied with the final copy.'
        }
      },
      {
        '@type': 'Question',
        name: 'What industries do you work with?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We specialize in restaurants, contractors, and service-based businesses in Chicago and West Michigan. If you serve customers and need better copy, we can help.'
        }
      },
      {
        '@type': 'Question',
        name: 'What\'s your pricing?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Project packages start at $750. Exact pricing depends on scope and timeline. Book a free consultation to get a custom quote.'
        }
      },
      {
        '@type': 'Question',
        name: 'Do you write in other languages?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Currently, we focus on English copywriting to ensure the highest quality and conversion rates.'
        }
      },
      {
        '@type': 'Question',
        name: 'What makes your approach different?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We don\'t use templates or AI-generated fluff. Every word is crafted specifically for your business, your customers, and your goals. We focus on authenticity and results, not just pretty words.'
        }
      }
    ]
  };
};

export const getServiceSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Professional Copywriting',
    provider: {
      '@type': 'ProfessionalService',
      name: 'The Pilot Copy',
      url: siteUrl
    },
    areaServed: [
      {
        '@type': 'City',
        name: 'Chicago'
      },
      {
        '@type': 'State',
        name: 'Michigan'
      }
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Copywriting Services',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Website Copywriting',
            description: 'Professional website copy that converts visitors into customers'
          },
          priceSpecification: {
            '@type': 'PriceSpecification',
            priceCurrency: 'USD',
            price: '750',
            minPrice: '750'
          }
        }
      ]
    }
  };
};
